import type { Languages } from './constants';

import * as cache from '@snowit/cache';
import { setSnowmentLocale } from '@snowit/utils/snowmentv2';

import en from '../../../localazy/en/snowit.json';
import it from '../../../localazy/it/snowit.json';
import { ALL_LANGUAGES } from './constants';

import i18next from 'i18next';

export const DEFAULT_LANGUAGE: Languages = 'it';
export const languages = ALL_LANGUAGES;

export const makeErrorString = (str?: string): string => {
  if (!str || typeof str !== 'string') return 'Error';
  if (/\s/.test(str)) return str;
  if (/^lzy\./.test(str)) return str;
  return ['lzy', 'error', str.toLowerCase().replace(/[/-]/g, '_')].join('.');
};

// i18next v26 only resolves the JSON v4 plural suffixes. Localazy still
// provides the legacy `key` / `key_plural` pairs, so expose both formats.
const normalizePlurals = (dictionary: Record<string, string>) => {
  const normalized = { ...dictionary };
  for (const [key, value] of Object.entries(dictionary)) {
    if (!key.endsWith('_plural')) continue;
    const baseKey = key.slice(0, -'_plural'.length);
    const singular = dictionary[baseKey];
    if (singular !== undefined) {
      normalized[`${baseKey}_one`] = singular;
      normalized[`${baseKey}_other`] = value;
    }
  }
  return normalized;
};

let initLanguage: Languages = DEFAULT_LANGUAGE;
const fromCache = cache.getLocal('language') as Languages;
if (fromCache) initLanguage = fromCache;

i18next.init({
  compatibilityJSON: 'v4',
  interpolation: { escapeValue: false },
  lng: initLanguage,
  fallbackLng: 'en',
  ns: ['common'],
  defaultNS: 'common',
  resources: {
    it: { common: { lzy: normalizePlurals(it) } },
    en: { common: { lzy: normalizePlurals(en) } },
    de: { common: { lzy: {} } },
    fr: { common: { lzy: {} } },
  },
});
setSnowmentLocale(initLanguage);

const saveToCache = (str: string | string[] | undefined): void => {
  if (!str) return;
  if (Array.isArray(str)) {
    str.forEach(saveToCache);
    return;
  }
  if (typeof str !== 'string' || !str.startsWith('lzy.')) return;
  if (process.env.SNOWIT_COMMAND !== 'start') return;
  const cached = JSON.parse(localStorage.getItem('lzy') ?? '[]') as string[];
  if (!cached.includes(str)) localStorage.setItem('lzy', JSON.stringify([...cached, str].sort()));
};

const translate = (key: string | string[], opts?: Record<string, any>) =>
  i18next.t(key as any, opts) as string;

export const t = (
  input?: string | string[],
  opts?: Record<string, any>,
  fallback?: string | string[],
  fallbackOpts?: Record<string, any>,
): string => {
  saveToCache(input);
  saveToCache(fallback);
  if (!input) return fallback ? t(fallback, fallbackOpts) : '';

  let translated = translate(input, opts);
  if (/^lzy\./.test(translated) && fallback) {
    translated = translate(fallback, fallbackOpts);
    if (/^lzy\./.test(translated)) return Array.isArray(input) ? input[0] : input;
  }
  return translated;
};

export const tr = (input: string, fallback: string) => t(input, {}, fallback);

export const tns = (str: string, namespace?: string) => {
  const fallback = str.replace(/\.DEFAULT\./, '.default.');
  if (!namespace) return t(fallback);
  return t(str.replace(/\.DEFAULT\./, `.${namespace}.`), {}, fallback);
};

export const te = (str?: string, opts?: Record<string, any>) => t(makeErrorString(str), opts);
export const exists = (key: string) => !/^lzy\./.test(t(key));
export const getLanguage = () => i18next.language as Languages;

export const changeLanguage = (language: Languages) => {
  if (language !== ('cimode' as Languages)) cache.setLocal('language', language);
  void i18next.changeLanguage(language);
  setSnowmentLocale(language);
  return language;
};
